import { DateTime } from 'luxon';
import { Period } from '../interfaces/Period';

export function normalizeToPeriod(d: Date, period: Period): Date {
  const dt = DateTime.fromJSDate(d);

  switch (period) {
    case Period.HOUR:
      return dt.startOf('hour').toJSDate();
    case Period.DAY:
      return dt.startOf('day').toJSDate();
    case Period.WEEK:
      return dt.startOf('week').toJSDate(); // Monday start
    case Period.MONTH:
      return dt.startOf('month').toJSDate();
    case Period.YEAR:
      return dt.startOf('year').toJSDate();
    default:
      return dt.toJSDate();
  }
}

export function getTicksBetweenRangeByPeriod(
  start: Date,
  end: Date,
  period: Period,
): Date[] {
  if (!start || !end) return [];

  const startDt = DateTime.fromJSDate(start);
  const endDt = DateTime.fromJSDate(end);

  if (startDt > endDt) return [];

  const align = (dt: DateTime): DateTime => {
    switch (period) {
      case Period.HOUR:
        return dt.startOf('hour');
      case Period.DAY:
        return dt.startOf('day');
      case Period.WEEK:
        return dt.startOf('week'); // Luxon default: Monday 👍
      case Period.MONTH:
        return dt.startOf('month');
      case Period.YEAR:
        return dt.startOf('year');
      default:
        return dt;
    }
  };

  const step = (dt: DateTime): DateTime => {
    switch (period) {
      case Period.HOUR:
        return dt.plus({ hours: 1 });
      case Period.DAY:
        return dt.plus({ days: 1 });
      case Period.WEEK:
        return dt.plus({ weeks: 1 });
      case Period.MONTH:
        return dt.plus({ months: 1 });
      case Period.YEAR:
        return dt.plus({ years: 1 });
      default:
        return dt;
    }
  };

  const ticks: Date[] = [];

  // first boundary >= start
  let cursor = align(startDt);

  while (cursor <= endDt) {
    ticks.push(cursor.toJSDate());
    cursor = step(cursor);
  }

  return ticks;
}

export function areInSamePeriod(a: Date, b: Date, period: Period): boolean {
  if (!a || !b) return false;

  const aDt = DateTime.fromJSDate(a);
  const bDt = DateTime.fromJSDate(b);

  const normalize = (dt: DateTime): DateTime => {
    switch (period) {
      case Period.HOUR:
        return dt.startOf('hour');
      case Period.DAY:
        return dt.startOf('day');
      case Period.WEEK:
        return dt.startOf('week'); // Monday start
      case Period.MONTH:
        return dt.startOf('month');
      case Period.YEAR:
        return dt.startOf('year');
      default:
        return dt;
    }
  };

  return normalize(aDt).toMillis() === normalize(bDt).toMillis();
}
